'use client'

import { motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import { Fragment, useCallback, useMemo, useState } from 'react'
import { Button } from '@/components/button'
import { DropdownMenu, IDropdownMenuItem } from '@/components/dropdown-menu'
import { IListItem, List } from '@/components/list'
import { SlidingPane } from '@/components/sliding-pane'
import { T } from '@/components/t'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { RestaurantQuerySorting } from '@/setup/urls/city-route'
import { run } from '@/utils/run'
import { COLORS } from '@/utils/styles/colors'
import { useMobile } from '@/utils/window/use-mobile'

/**
 * Sorting dropdown for restaurant city search
 */
export const RestaurantCitySearchSorting = () => {
    const [isOpen, setIsOpen] = useState(false)

    const {
        query: { sortBy },
        setters: { setSortBy },
    } = useRestaurantCitySearchContext()

    const t = useTranslations()

    const toggleIsOpen = useCallback(() => setIsOpen((prev) => !prev), [])

    const items = useMemo((): (IDropdownMenuItem<RestaurantQuerySorting> &
        IListItem<RestaurantQuerySorting>)[] => {
        return [
            {
                label: t('restaurants.city.sorting.options.recommended'),
                id: RestaurantQuerySorting.RECOMMENDED,
                value: RestaurantQuerySorting.RECOMMENDED,
                onClick: () => setSortBy(RestaurantQuerySorting.RECOMMENDED),
                selected: sortBy === RestaurantQuerySorting.RECOMMENDED,
            },
            {
                label: t('restaurants.city.sorting.options.highestPopularity'),
                value: RestaurantQuerySorting.POPULARITY,
                id: RestaurantQuerySorting.POPULARITY,
                onClick: () => setSortBy(RestaurantQuerySorting.POPULARITY),
                selected: sortBy === RestaurantQuerySorting.POPULARITY,
            },
            {
                label: t('restaurants.city.sorting.options.bestRating'),
                value: RestaurantQuerySorting.RATING,
                id: RestaurantQuerySorting.RATING,
                onClick: () => setSortBy(RestaurantQuerySorting.RATING),
                selected: sortBy === RestaurantQuerySorting.RATING,
            },
            {
                label: t('restaurants.city.sorting.options.biggestDiscount'),
                value: RestaurantQuerySorting.DISCOUNT,
                id: RestaurantQuerySorting.DISCOUNT,
                onClick: () => setSortBy(RestaurantQuerySorting.DISCOUNT),
                selected: sortBy === RestaurantQuerySorting.DISCOUNT,
            },
            {
                label: t('restaurants.city.sorting.options.newest'),
                value: RestaurantQuerySorting.NEWEST,
                id: RestaurantQuerySorting.NEWEST,
                onClick: () => setSortBy(RestaurantQuerySorting.NEWEST),
                selected: sortBy === RestaurantQuerySorting.NEWEST,
            },
        ]
    }, [t, setSortBy, sortBy])

    const isMobile = useMobile()

    // Get the selected item from the list of items
    const selectedItem = useMemo(() => items.find((item) => item.selected), [items])

    return (
        <div className="md:flex flex-row items-center select-none">
            <T t="restaurants.city.sorting.label" className="hidden md:block" />

            <div className="relative md:z-[1]">
                <Button onClick={toggleIsOpen} styleType="invisible">
                    <span className="font-semibold mr-1">{selectedItem?.label}</span>

                    <motion.div
                        initial={{ rotate: isOpen ? 270 : 90 }}
                        animate={{ rotate: isOpen ? 270 : 90 }}
                    >
                        <SvgOutlinedChevronRight
                            color={COLORS.black}
                            size={24}
                            style={{ position: 'relative', top: '-1px' }}
                        />
                    </motion.div>
                </Button>

                {run(() => {
                    if (isMobile === undefined) return null

                    // Only show the dropdown on desktop
                    if (!isMobile) {
                        return (
                            <Fragment>
                                <div className="relative">
                                    <DropdownMenu
                                        items={items}
                                        isOpen={isOpen}
                                        setIsOpen={setIsOpen}
                                    />
                                </div>

                                {/* Invisible background which closes the dropdown when clicked. Only there when dropdown is open */}
                                {isOpen && (
                                    <div
                                        onClick={() => setIsOpen(false)}
                                        className="fixed min-h-dvh min-w-[100vw] top-0 left-0 bg-transparent z-[-1]"
                                    />
                                )}
                            </Fragment>
                        )
                    }

                    return (
                        <SlidingPane
                            sizeType="fit"
                            onClose={() => setIsOpen(false)}
                            isOpen={isOpen}
                        >
                            <List<RestaurantQuerySorting>
                                list={items}
                                onClick={() => setIsOpen(false)}
                                className="px-5"
                            />
                        </SlidingPane>
                    )
                })}
            </div>
        </div>
    )
}
